import { useState, useEffect, useCallback } from "react";

// - - - - - - - - - -
// Generic draggable vertical splitter (#104).
// Designed for reuse: the side-by-side grids feature (#105) will use the
// same hook + handle for the grid-vs-grid divider.

const DEFAULT_MIN_WIDTH = 160;
const DEFAULT_MAX_WIDTH = 600;
const DEFAULT_WIDTH = 240; // matches the original w-60 sidebar

/**
 * useSplitterWidth
 * Tracks a resizable width with min/max clamping and localStorage persistence.
 * Persists on drag end (not on every pixel) to avoid hammering localStorage.
 *
 * @param {string} storageKey - localStorage key for persisting the width
 * @param {Object} [options]
 * @param {number} [options.defaultWidth=240]
 * @param {number} [options.minWidth=160]
 * @param {number} [options.maxWidth=600]
 */
export const useSplitterWidth = (storageKey, options = {}) => {
  const {
    defaultWidth = DEFAULT_WIDTH,
    minWidth = DEFAULT_MIN_WIDTH,
    maxWidth = DEFAULT_MAX_WIDTH,
  } = options;

  const [width, setWidth] = useState(() => {
    try {
      const stored = parseInt(localStorage.getItem(storageKey) ?? "", 10);
      if (Number.isFinite(stored)) {
        return Math.min(maxWidth, Math.max(minWidth, stored));
      }
    } catch (e) {
      // localStorage unavailable (private mode, quota, etc.) — fall through
    }
    return defaultWidth;
  });

  const [isDragging, setIsDragging] = useState(false);

  // - - - - - - - - - -
  // Drag tracking: bind global mousemove/mouseup while dragging
  useEffect(() => {
    if (!isDragging) return;

    const onMouseMove = (e) => {
      setWidth(Math.min(maxWidth, Math.max(minWidth, e.clientX)));
    };
    const onMouseUp = () => setIsDragging(false);

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isDragging, minWidth, maxWidth]);

  // - - - - - - - - - -
  // Persist on drag end (not on every pixel) to avoid hammering localStorage
  useEffect(() => {
    if (isDragging) return;
    try {
      localStorage.setItem(storageKey, String(width));
    } catch (e) {
      // ignore quota / private-mode errors
    }
  }, [isDragging, width, storageKey]);

  const startDrag = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  return { width, isDragging, startDrag };
};

// - - - - - - - - - -
// Visual splitter handle. Sits between two flex children of a flex container.
// Subtle accent when idle, brighter on hover/drag, col-resize cursor.

export const Splitter = ({ onMouseDown, isDragging, center = false }) => {
  const [isHover, setIsHover] = useState(false);
  const active = isDragging || isHover;

  // Outer wrapper provides an 8px hit area so the splitter is easy to grab,
  // but the visible bar inside is only 2px wide to match the yellow
  // border-b-2 divider used elsewhere in the header.
  // `center=true` puts the visible 2px bar in the middle of the hit area
  // (grid-vs-grid splitter where both sides are equivalent). Default
  // `justify-start` puts the bar on the left edge, used for the RTS
  // sidebar splitter where the line should hug the dark/light boundary.
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize sidebar"
      onMouseDown={onMouseDown}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      className={`w-2 shrink-0 cursor-col-resize flex items-stretch ${
        center ? "justify-center" : "justify-start"
      }`}
    >
      <div
        className="w-0.5 h-full"
        style={{
          backgroundColor: active
            ? "rgb(var(--color-button-accent))"
            : "rgba(89, 89, 89, 0.4)",
          transition: isDragging ? "none" : "background-color 150ms",
        }}
      />
    </div>
  );
};

export default Splitter;
